import {
    filter,
    groupBy,
    isEmpty,
    map,
    sortBy,
    trimEnd,
    chunk,
} from 'lodash';

export default {

    /**
     * Functional getter for user
     *
     * @param {object} state Vuex state.
     *
     * @returns {object} User data
     */
    getUser: (state) => (key) => {
        return (state.user || {})[key] || null;
    },

    /**
     * Retursn avatar URL
     *
     * @param {object} state Vuex state.
     *
     * @returns {string} Avatar url
     */
    avatarUrl(state) {
        return state.user?.meta_data?.avatar || false;
    },

    /**
     * Prepares sorted home widgets.
     *
     * @param {object} state State object.
     * @returns {object} Sorted home widgets.
     */
    sortedHomeWidgets(state) {
        const allowedTypes = [
            'all',
            (state.user.pro ? 'pro' : 'free'),
        ];

        return map(
            groupBy(
                filter(
                    state.homeWidgets,
                    (item) => allowedTypes.includes(item.subscriptionType)
                ),
                'column'
            ),
            (column) => sortBy(column, 'order')
        );
    },

    /**
     * Returns all/free seats summed from all licenses.
     *
     * @param   {object} state State object.
     * @returns {object|boolean}       Total seats.
     */
    totalLicenseSeats(state) {
        if (state.licenses && (state.currentSubscription || ! state.user.pro)) {
            const seats = {
                all: 0,
                free: 0,
                used: 0,
            };

            for (const license of state.licenses) {
                if (state.user.pro && state.currentSubscription.id !== license.subscription_id) {
                    // Only count licenses in current subscription for pro/lifetime
                    continue;
                }

                seats.all += license.seats;
                seats.free += license.free_seats;
                seats.used += license.used_seats;
            }

            return seats;
        }

        return false;
    },

    /**
     * Functional getter for user meta.
     *
     * Usage:
     *      store.getters.getUserMeta('meta_key');
     *
     * @param   {object} state State.
     * @returns {Function}     Getter function.
     */
    getUserMeta: (state) => (key) => {
        return (state.user.meta_data || {})[key] || null;
    },

    /**
     * Return websites list
     *
     * @param  {object} state State.
     * @returns {Array|boolean}  Websites array or false.
     */
    websites(state) {
        if (state.licenses) {
            const websites = [];

            for (const license of state.licenses) {
                if (1 < license.seats) {
                    for (const website of license.websites) {
                        website.home = trimEnd(website.home, '/');
                        websites.push(website);
                    }
                }
            }

            if (! isEmpty(websites)) {
                return websites;
            }
        }

        return false;
    },

    /**
     * Returns user card
     *
     * @param {object} state Vuex state.
     *
     * @returns {object} User card data.
     */
    userCard(state) {
        if (! isEmpty(state.userCard)) {
            const card = state.userCard;

            let month = card.exp_month;
            const year = card.exp_year;
            const brand = card.brand;

            if (2 > month.toString().length) {
                month = '0' + month;
            }

            card.expiryDate = `${month}/${year}`;

            if ('visa' === brand || 'mastercard' === brand) {
                card.brandClass = `fab fa-cc-${brand}`;
            } else {
                card.brandClass = 'fal fa-credit-card';
            }

            return card;
        }

        return state.userCard;
    },

    /**
     * Returns user subscription details
     *
     * @param   {object}  state  Vuex state.
     *
     * @returns  {object}         Subscription details.
     */
    userSubscriptionDetails(state) {
        if (! isEmpty(state.userSubscription)) {
            const { discount, name } = state.userSubscription;
            const subscriptionDetails = state.userSubscription.items.data[0];
            const interval = subscriptionDetails.plan.interval;
            const couponPercent = discount?.coupon?.percent_off || null;
            const couponAmount = discount?.coupon?.amount_off || null;
            let nickname = subscriptionDetails.plan.nickname;
            let amount = subscriptionDetails.plan.amount * subscriptionDetails.quantity;

            if ('lifetime' === name) {
                nickname = 'Lifetime';
                amount = 0;
            } else {
                if (couponPercent) {
                    amount -= Math.round(amount * couponPercent / 100, 2);
                } else if (couponAmount) {
                    amount -= couponAmount;
                }
            }

            if (isEmpty(nickname)) {
                switch (interval) {
                case 'month':
                    nickname = 'Monthly';
                    break;
                case 'year':
                    nickname = 'Yearly';
                    break;
                }
            }

            return {
                subscriptionDetails,
                amount,
                nickname,
                name,
                couponPercent,
                couponAmount,
                interval,
                quantity: subscriptionDetails.quantity,
                couponCode: discount?.coupon?.id || null,
                endsAt: state.userSubscription.cancel_at,
            };
        }

        return {};
    },

    /**
     * Returns user upcoming invoice
     *
     * @param {object} state Vuex state.
     *
     * @returns  {object}         Upcoming invoice.
     */
    upcomingInvoice(state) {
        return state.upcomingInvoice || false;
    },

    /**
     * Return true if current subscription is lifetime, false otherwise.
     *
     * @param  {object}   state State object.
     * @returns {boolean} Whether current subscription is lifetime.
     */
    isLifetimeSubscriptionSelected(state) {
        return 'lifetime' === state.currentSubscription?.name;
    },

    /**
     * Get product features split into two columns
     *
     * @param  {object} state Vuex State
     * @returns {Array}  Product features.
     */
    groupedProductFeatures(state) {
        return map(state.products, product => chunk(
            product.feature_list,
            Math.ceil(product.feature_list.length / 2)
        ));
    },
};
